import { useMemo } from "react";
import { useCatalog } from "@/data/CatalogContext";
import type { ProjectConfiguration } from "@/data/types";
import { Badge } from "@/components/ui/badge";
import {
  Layers, RectangleHorizontal, Palette, Wrench, GlassWater,
  DoorOpen, Grip, Sparkles, Package, AlertTriangle, CheckCircle,
} from "lucide-react";

interface Props {
  config: ProjectConfiguration;
  isBathroom: boolean;
}

interface SummaryRow {
  label: string;
  value: string | null;
  required: boolean;
  icon: React.ElementType;
}

export function DesignBuildSummary({ config, isBathroom }: Props) {
  const { getProductsByCategory, getActiveVariants } = useCatalog();

  const rows: SummaryRow[] = useMemo(() => {
    if (!isBathroom) return [];

    // Resolve wall system name
    const catalogWallSystems = getProductsByCategory("wall_system");
    const wallSystemProduct = catalogWallSystems.find(
      p => p.materialFilter === config.bathroomMaterials[0]
    );

    // Resolve wall color from variant
    const wallColorVariants = wallSystemProduct
      ? getActiveVariants(wallSystemProduct.id)
      : [];
    const wallColorName = config.wallColor || null;

    // Resolve base
    const catalogBases = getProductsByCategory("base", config.bathroomMaterials[0]);
    const baseName = config.baseType || null;

    // Fixtures
    const catalogFixtures = getProductsByCategory("fixture");
    const fixtureName = catalogFixtures.find(f => f.id === config.selectedFixture)?.displayName || null;

    // Grab bars
    const catalogGrabBars = getProductsByCategory("grab_bar");
    const grabBarNames = config.selectedGrabBars
      .map(id => catalogGrabBars.find(g => g.id === id)?.displayName || id)
      .filter(Boolean);

    // Add-ons
    const catalogAddOns = getProductsByCategory("add_on");
    const addOnNames = config.selectedAddOns
      .map(name => catalogAddOns.find(a => a.displayName === name)?.displayName || name)
      .filter(Boolean);
    const customNames = (config.customAddOns || []).filter(c => c.name).map(c => c.name);
    const allAddOns = [...addOnNames, ...customNames];

    return [
      {
        label: "Wall System",
        value: wallSystemProduct?.displayName || null,
        required: true,
        icon: Layers,
      },
      {
        label: "Wall Color",
        value: wallColorName,
        required: false,
        icon: Palette,
      },
      {
        label: "Base Type",
        value: baseName,
        required: true,
        icon: RectangleHorizontal,
      },
      {
        label: "Hardware Finish",
        value: config.hardwareFinish || null,
        required: false,
        icon: Wrench,
      },
      {
        label: "Enclosure",
        value: config.enclosureType || null,
        required: true,
        icon: GlassWater,
      },
      {
        label: "Fixtures",
        value: fixtureName,
        required: false,
        icon: DoorOpen,
      },
      {
        label: "Grab Bars",
        value: grabBarNames.length > 0 ? grabBarNames.join(", ") : null,
        required: false,
        icon: Grip,
      },
      {
        label: "Add-ons",
        value: allAddOns.length > 0 ? allAddOns.join(", ") : null,
        required: false,
        icon: Sparkles,
      },
    ];
  }, [config, isBathroom, getProductsByCategory, getActiveVariants]);

  if (!isBathroom) return null;

  const selectedCount = rows.filter(r => r.value).length;
  const totalCount = rows.length;
  const missingRequired = rows.filter(r => r.required && !r.value);
  const missingOptional = rows.filter(r => !r.required && !r.value);

  return (
    <div className="rounded-xl border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-primary" />
          <span className="text-xs font-bold text-foreground uppercase tracking-wider">Build Summary</span>
        </div>
        <Badge variant={selectedCount === totalCount ? "default" : "secondary"} className="text-[10px] h-5">
          {selectedCount}/{totalCount} selected
        </Badge>
      </div>

      <div className="space-y-1.5">
        {rows.map(row => {
          const Icon = row.icon;
          const isSelected = !!row.value;
          return (
            <div
              key={row.label}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors ${
                isSelected
                  ? "bg-primary/5 border border-primary/20"
                  : row.required
                    ? "bg-destructive/5 border border-destructive/20"
                    : "bg-muted/30 border border-transparent"
              }`}
            >
              <Icon className={`h-3.5 w-3.5 shrink-0 ${
                isSelected ? "text-primary" : row.required ? "text-destructive" : "text-muted-foreground/50"
              }`} />
              <div className="flex-1 min-w-0">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block">
                  {row.label}
                  {row.required && !isSelected && <span className="text-destructive ml-0.5">*</span>}
                </span>
                {isSelected ? (
                  <span className="text-xs font-medium text-foreground truncate block">{row.value}</span>
                ) : (
                  <span className="text-[11px] text-muted-foreground/60 italic">
                    {row.required ? "Required — please select" : "Not selected"}
                  </span>
                )}
              </div>
              {isSelected ? (
                <CheckCircle className="h-3.5 w-3.5 text-primary shrink-0" />
              ) : row.required ? (
                <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Summary footer */}
      {missingRequired.length > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-destructive/5 border border-destructive/20">
          <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
          <span className="text-[11px] text-destructive font-medium">
            {missingRequired.length} required {missingRequired.length === 1 ? "selection" : "selections"} missing
          </span>
        </div>
      )}
      {missingRequired.length === 0 && missingOptional.length > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-warning/5 border border-warning/20">
          <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
          <span className="text-[11px] text-warning font-medium">
            {missingOptional.length} optional {missingOptional.length === 1 ? "item" : "items"} not selected
          </span>
        </div>
      )}
      {missingRequired.length === 0 && missingOptional.length === 0 && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-success/5 border border-success/20">
          <CheckCircle className="h-3.5 w-3.5 text-success shrink-0 mt-0.5" />
          <span className="text-[11px] text-success font-medium">Build configuration complete</span>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   Validation Logic — exported for use in flow
   ═══════════════════════════════════════════════════ */

export interface DesignValidationResult {
  canProceed: boolean;
  criticalMissing: string[];
  recommendedMissing: string[];
}

export function validateDesignStep(config: ProjectConfiguration, isBathroom: boolean): DesignValidationResult {
  if (!isBathroom) return { canProceed: true, criticalMissing: [], recommendedMissing: [] };

  const critical: string[] = [];
  const recommended: string[] = [];

  // Critical
  if (config.bathroomMaterials.length === 0) critical.push("Wall System");
  if (!config.baseType) critical.push("Base Type");
  if (!config.enclosureType) critical.push("Enclosure Type");

  // Recommended
  if (config.selectedGrabBars.length === 0) recommended.push("Grab Bars");
  // "No Add-Ons" is a valid selection — only flag if nothing was explicitly chosen
  // We check for a noAddOns flag on the config, or if addons/customAddOns exist
  if (config.selectedAddOns.length === 0 && (!config.customAddOns || config.customAddOns.length === 0) && !(config as any).noAddOnsSelected)
    recommended.push("Add-ons");
  if (!config.selectedFixture) recommended.push("Fixture Package");
  if (!config.hardwareFinish) recommended.push("Hardware Finish");

  return {
    canProceed: critical.length === 0,
    criticalMissing: critical,
    recommendedMissing: recommended,
  };
}
